/* Pure functions to help with e.g. data conversion */
import { createSelector } from "@reduxjs/toolkit";
import { Feature, LineString, Point, Polygon as GeoJSONPolygon } from "geojson";
import L from "leaflet";
import { latLng, latLngBounds, LatLngBounds, LatLngLiteral } from "leaflet";
import { difference } from "lodash";
import {
  HasId,
  Image,
  Indication,
  MultilineCell,
  Polygon,
  UpdateValueWithMaybeReasonForChange,
  UUID,
  ZoomDefinition,
  MultilineCategory,
  MultilineAnnotation,
  ImageWithAnnotations,
  Fragment,
  HpfAnno,
  Annotation,
  HPFAnnotation,
  PointAnno,
  PointAnnotation,
  FreehandAnnotation,
  AnnotationClassType,
  AnnotationClassWithCount,
  MultilineAnnotationClassesWithCount,
  PointAnnotationClassesWithCount
} from "./models";

import { isThisYear, isToday, parse } from "date-fns";
import format from "date-fns/format";
import { extname } from "path";
import { AnnotationType } from "./slices/caseImageViewer";
import { AnnotationClassForm } from "./slices/studyConfiguration";
import { RootState } from "./store";

export const LOCALE = "en-CA";

// Stroke width (px) of a multiline (VH/CD) annotation, shared by the saved line and the in-progress
// line while drawing so the two match (LUC-303). Leaflet strokes are screen pixels, so the width is
// scaled with zoom: MULTILINE_WEIGHT fully zoomed in, narrowing linearly to MULTILINE_MIN_WEIGHT fully
// zoomed out so lines do not crowd the overview.
export const MULTILINE_WEIGHT = 10;
export const MULTILINE_MIN_WEIGHT = 5;

export function multilineWeight(zoom: number, minZoom: number, maxZoom: number): number {
  const range = maxZoom - minZoom;
  const fraction = range <= 0 ? 1 : Math.min(1, Math.max(0, (zoom - minZoom) / range));
  return MULTILINE_MIN_WEIGHT + fraction * (MULTILINE_WEIGHT - MULTILINE_MIN_WEIGHT);
}

export function pointToGeoJSON(latLng: LatLngLiteral): Feature<Point> {
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [latLng.lat, latLng.lng]
    },
    properties: {}
  };
}

export function lineToGeoJSON(latLngs: ReadonlyArray<LatLngLiteral>): Feature<LineString> {
  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: latLngs.map(l => [l.lat, l.lng])
    },
    properties: {}
  };
}

export function polygonToGeoJSON(latLngs: ReadonlyArray<LatLngLiteral>): Feature<GeoJSONPolygon> {
  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [latLngs.map(l => [l.lat, l.lng])]
    },
    properties: {}
  };
}

export function toLatLngBounds(poly: Polygon): LatLngBounds | undefined {
  const coords = poly.coordinates[0]?.map(arr => latLng(arr[1], arr[0]));
  return coords && latLngBounds(coords);
}

export function getMaxZoom(zooms: ReadonlyArray<ZoomDefinition>): number {
  return zooms.map(obj => obj.zoom).reduce((x, y) => Math.max(x, y), 0);
}

export function getMinZoom(zooms: ReadonlyArray<ZoomDefinition>): number {
  return zooms.map(obj => obj.zoom).reduce((x, y) => Math.min(x, y), 30);
}

/*
 * HL7 timestamps may be truncated to any precision (YYYY[MM[DD[HH[MM[SS]]]]]), and neither the HL7
 * ingestion nor the manual Sample Data CSV import checks the format of specimenCollectionDate, so
 * accept each precision down to the day rather than only the full 14 digits. The value must be all
 * digits and exactly as long as the format (the formats have no literals): date-fns does not
 * enforce per-token digit counts, so without that check a truncated "2023011" would be read as a
 * plausible but wrong 2023-01-01. Year- and month-only values are deliberately NOT parsed: a Date
 * needs a day, so "2023" would display as a fabricated Jan 1. Anything unparsed returns null rather
 * than an Invalid Date (which date-fns format() throws on); use formatLISDate for display, which falls
 * back to the raw string.
 */
const LIS_DATE_FORMATS: ReadonlyArray<string> = [
  "yyyyMMddHHmmss",
  "yyyyMMddHHmm",
  "yyyyMMddHH",
  "yyyyMMdd"
];

export function parseLISDateString(dateString: string | null): Date | null {
  if (dateString === null || !/^\d+$/.test(dateString)) {
    return null;
  }
  const dateFormat = LIS_DATE_FORMATS.find(f => f.length === dateString.length);
  const date = dateFormat ? parse(dateString, dateFormat, new Date()) : null;
  return date && !isNaN(date.getTime()) ? date : null;
}

/*
 * Display form of a stored LIS date: the formatted date when it parses, otherwise the stored string
 * as-is, so an unparseable value is never shown as "—" (which should mean the field is empty).
 */
export function formatLISDate(dateString: string | null): string {
  const date = parseLISDateString(dateString);
  return date ? formatDate(date) : dateString?.trim() || "—";
}

export function formatDate(date: Date | null): string {
  return date instanceof Date
    ? isToday(date)
      ? format(date, "h:mma")
      : isThisYear(date)
      ? format(date, "MMM d")
      : format(date, "MMM d yyyy")
    : "—";
}

export function formateDateToYearMonthDay(date: Date | null): string {
  return date instanceof Date
    ? date.toLocaleDateString(LOCALE, { month: "long", day: "numeric", year: "numeric" })
    : "-";
}

export function formateDateToYearMonthDayTime(date: Date | null): string {
  return date instanceof Date
    ? date.toLocaleDateString(LOCALE, {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZoneName: "short"
      })
    : "-";
}

export function getDaysFromDateSelected(date: Date): number {
  const today = new Date();
  const differenceMs = date.getTime() - today.getTime();
  const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

  return differenceDays;
}

function distanceToMeters(distance: number, pixelSize: number, resolutionInMeters: number): number {
  return (distance * resolutionInMeters) / pixelSize;
}

export function metersToMicrometers(meters: number): number {
  return meters * 10 ** 6;
}

export function toMicrometers(
  distance: number,
  pixelSize: number,
  resolutionInMeters: number
): number {
  const meters = distanceToMeters(distance, pixelSize, resolutionInMeters);
  return metersToMicrometers(meters);
}

export function formatMicrometers(micrometers: number): string {
  return `${Math.floor(micrometers)} μm`;
}

/**
 * Round to two decimals with HALF_UP, matching the backend's round2
 * (CeliacScoreService.round2 = BigDecimal(d.toString).setScale(2, HALF_UP)). We round the number's
 * shortest round-tripping decimal string — the same value `d.toString` yields on the JVM — rather
 * than using toFixed(2), whose IEEE-754 rounding direction disagrees at .xx5 boundaries (e.g.
 * (1.005).toFixed(2) === "1.00", but the backend produces "1.01" for the same measurement). Keeping
 * this in one helper avoids the divergence being re-introduced at each new score call site.
 */
export function round2HalfUp(n: number): string {
  const negative = n < 0;
  const [intPart, fracPart = ""] = String(Math.abs(n)).split(".");
  if (fracPart.length <= 2) {
    return (negative ? "-" : "") + Number(`${intPart}.${fracPart}`).toFixed(2);
  }
  // HALF_UP: round the two-decimal value up when the first dropped digit is >= 5.
  const kept = Number(`${intPart}.${fracPart.slice(0, 2)}`);
  const roundUp = Number(fracPart[2]) >= 5;
  const rounded = roundUp ? kept + 0.01 : kept;
  return (negative && rounded !== 0 ? "-" : "") + rounded.toFixed(2);
}

/**
 * A villus height or crypt depth length for display, always with exactly two decimals (LUC-293),
 * e.g. "45.60 μm". Uses the same HALF_UP rounding as the score ratios, which also matches the
 * Postgres ROUND(..., 2) in the villi measurement report.
 */
export function formatVhCdLength(micrometers: number): string {
  return `${round2HalfUp(micrometers)} μm`;
}

/**
 * The villus height and crypt depth lines for one annotation class (one villus/crypt unit), paired
 * exactly as the backend `CeliacScoreDao.vhCdMeasurements` does: when a retry/double-click race has
 * left two lines for the same (class, category), take the LONGEST (`MAX(ST_LENGTH)`), because a
 * stray click-without-drag leaves a near-zero-length line that must not be allowed to win. `find`
 * (first in array order) is NOT equivalent — it can pick the stray line. Shared by the villus/crypt
 * table and the case-level average so the pairing rule lives in one place.
 */
export function vhCdForClass(
  annotationClassId: string,
  multilineAnnotations: ReadonlyArray<MultilineAnnotation>
): { villusHeight: number | undefined; cryptDepth: number | undefined } {
  const longestForCategory = (category: MultilineCategory): number | undefined =>
    multilineAnnotations
      .filter(a => a.annotationClassId == annotationClassId && a.multilineCategory == category)
      .map(a => a.length)
      .filter((length): length is number => length !== null)
      .reduce<number | undefined>(
        (longest, length) => (longest === undefined || length > longest ? length : longest),
        undefined
      );
  return {
    villusHeight: longestForCategory(MultilineCategory.VillusHeight),
    cryptDepth: longestForCategory(MultilineCategory.CryptDepth)
  };
}

/**
 * The read-only vh:cd ratio for one villus/crypt unit = villus height / crypt depth, rounded to two
 * decimals with HALF_UP (matching the backend per-unit display ratio in CeliacScoreService — same
 * rounding direction and same zero-crypt guard). Returns "" (blank) only when a quotient can't be
 * formed: a measurement missing (null/undefined) or crypt depth not strictly positive
 * (divide-by-zero). A villus height of exactly 0 with a positive crypt depth is a valid ratio of
 * "0.00", matching the backend's `case (Some(v), Some(c)) if c > 0.0` (which guards only the crypt).
 */
export function formatVhCdRatio(
  villusHeight: number | null | undefined,
  cryptDepth: number | null | undefined
): string {
  return villusHeight !== null &&
    villusHeight !== undefined &&
    cryptDepth !== null &&
    cryptDepth !== undefined &&
    cryptDepth > 0
    ? round2HalfUp(villusHeight / cryptDepth)
    : "";
}

// The point annotation-class names used for the IEL ratio on Celiac studies. Must match the backend
// seeds referenced by CeliacScoreDao (IelClassName / EnterocyteClassName).
const IEL_CLASS_NAME = "IEL";
const ENTEROCYTE_CLASS_NAME = "Enterocyte";

/**
 * Case-level vh:cd average = the mean of the per-unit villus:crypt ratios, paired by annotation
 * class exactly as the villus/crypt table does, formatted to two decimals. Mirrors the backend
 * `CeliacScoreService`: only units with both a villus and a non-zero crypt line contribute a ratio;
 * the full-precision ratios are averaged and the average is rounded once (rounding each ratio first
 * would accumulate error). Returns "" (blank) when no unit yields a ratio.
 */
export function celiacVhCdAverage(
  annotationClasses: MultilineAnnotationClassesWithCount,
  annotations: ReadonlyArray<Annotation>
): string {
  const multilineAnnotations = annotations.filter(
    a => a.annotationType === AnnotationType.Multiline
  ) as ReadonlyArray<MultilineAnnotation>;

  const unitRatios = annotationClasses
    .map(({ annotationClass }) => {
      // Pair via the shared helper (longest-line dedup, matching the backend); see vhCdForClass.
      const { villusHeight, cryptDepth } = vhCdForClass(annotationClass.id, multilineAnnotations);
      // Match the backend's `case (Some(v), Some(c)) if c > 0.0`: guard the crypt against
      // divide-by-zero only. A villus height of exactly 0 is a valid unit contributing 0.0 — using a
      // truthy check would drop it, changing both the sum and the divisor of the average.
      return villusHeight !== undefined && cryptDepth !== undefined && cryptDepth > 0
        ? villusHeight / cryptDepth
        : null;
    })
    .filter((ratio): ratio is number => ratio !== null);

  return unitRatios.length === 0
    ? ""
    : round2HalfUp(unitRatios.reduce((sum, r) => sum + r, 0) / unitRatios.length);
}

/**
 * Case-level IEL Ratio = IELs per 100 enterocytes (IEL points × 100 ÷ enterocyte points, LUC-309),
 * formatted to two decimals. Mirrors the backend `CeliacScoreService.ielRatio`: AI (Visiopharm)
 * points are counted only when the reader has the "include AI annotations in reports" toggle on
 * (`aiAccepted`), otherwise CR points only.
 * Only points inside a freehand region count (LUC-284, mirrors `CeliacScoreDao.insideReaderFreehand`);
 * a point inside overlapping regions counts once. Returns "" (blank) when there are no counted
 * enterocyte points (divide-by-zero), including when no freehand region has been drawn.
 */
export function celiacIelRatio(
  pointClasses: PointAnnotationClassesWithCount,
  annotations: ReadonlyArray<Annotation>,
  aiAccepted: boolean
): string {
  const idsFor = (className: string) =>
    pointClasses
      .filter(({ annotationClass }) => annotationClass.name === className)
      .map(({ annotationClass }) => annotationClass.id);
  const ielClassIds = idsFor(IEL_CLASS_NAME);
  const enterocyteClassIds = idsFor(ENTEROCYTE_CLASS_NAME);
  const freehands = annotations.filter(
    a => a.annotationType === AnnotationType.Freehand
  ) as ReadonlyArray<FreehandAnnotation>;

  const countPoints = (classIds: ReadonlyArray<string>) =>
    annotations.filter(
      a =>
        a.annotationType === AnnotationType.Point &&
        (aiAccepted || !a.isAi) &&
        (a as PointAnnotation).annotationClassId !== null &&
        classIds.includes((a as PointAnnotation).annotationClassId as string) &&
        freehands.some(freehand => isPointInFreehand(a as PointAnnotation, freehand))
    ).length;

  const iel = countPoints(ielClassIds);
  const enterocyte = countPoints(enterocyteClassIds);

  return enterocyte === 0 ? "" : round2HalfUp((iel * 100) / enterocyte);
}

/**
 * Calculate the number of micrometers (μm) corresponding to a given distance on current map.
 */
export function mapDistanceToMicrometers(
  map: L.Map,
  distance: number,
  pixelSize: number,
  resolutionInMeters: number
): number {
  // NOTE: Borrowed logic from built in Leaflet scale control
  // See: https://github.com/Leaflet/Leaflet/blob/37d2fd15ad6518c254fae3e033177e96c48b5012/src/control/Control.Scale.js#L68-L73
  const y = map.getSize().y / 2;
  const width = map.distance(
    map.containerPointToLatLng([0, y]),
    map.containerPointToLatLng([distance, y])
  );
  return metersToMicrometers(distanceToMeters(width, pixelSize, resolutionInMeters));
}

export function getHpfRadiusInMeters(
  pixelSize: number,
  resolutionInMeters: number,
  indications: ReadonlyArray<Indication> | null
): number {
  // Most studies use a standard zoom of 40x which is 0.238mm² known as 1 HPF
  // NASH studies use a 20x zoom
  const hpfFactor =
    indications && indications.includes(Indication.NonalcoholicSteatohepatitis) ? 2 : 1;
  const hpfAreaInSquareMeters = 0.238 * 10 ** -6;
  const hpfRadiusInMeters = Math.sqrt(hpfAreaInSquareMeters / Math.PI); // radius = sqrt(Area / PI)
  return (hpfFactor * (hpfRadiusInMeters * pixelSize)) / resolutionInMeters;
}

/*
 * Test whether sets of ids are different to detect changes in form input.
 */
export function areDifferent<T extends string | number>(
  a: ReadonlyArray<T>,
  b: ReadonlyArray<T>
): boolean {
  return a.length !== b.length
    ? true
    : difference(Array.from(a).sort(), Array.from(b).sort()).length > 0;
}

/*
 * Creates a name for a duplicate image
 */
export function duplicateImageName(imageName: string, suffix: string = "_d"): string {
  return imageName.replace(extname(imageName), `${suffix}${extname(imageName)}`);
}

/*
 * Creates an accession number for a duplicate image
 */
export function duplicateAccessionNumber(
  accessionNumber: string | null,
  suffix: string = "_d"
): string | null {
  return accessionNumber ? `${accessionNumber}${suffix}` : null;
}

export function idsOnly(haveId: readonly HasId[]): UUID[] {
  return haveId.map(({ id }) => id);
}

export function updateWithIdsOnly({
  value,
  reasonForChange
}: UpdateValueWithMaybeReasonForChange<readonly HasId[]>): UpdateValueWithMaybeReasonForChange<
  UUID[]
> {
  return {
    value: idsOnly(value),
    ...(reasonForChange ? { reasonForChange } : {})
  };
}

export function getNextUnusedMultilineCell(
  imageWithAnnotations: ImageWithAnnotations,
  exclude?: MultilineCell,
  start?: MultilineCell
): MultilineCell | null {
  const annotations = imageWithAnnotations.annotations;

  const multilineAnnotations = annotations.filter(
    annotation => annotation.annotationType == AnnotationType.Multiline
  ) as MultilineAnnotation[];

  const multilineAnnotationClassesWithCount = imageWithAnnotations.multilineAnnotationClassesWithCount.filter(
    awc => awc.annotationClass.enabled
  );

  if (multilineAnnotationClassesWithCount.length === 0) {
    return null;
  }

  const categories = [MultilineCategory.VillusHeight, MultilineCategory.CryptDepth];

  const hasStart = start && start.class !== null && start.category !== null;

  const startIndexClass = hasStart
    ? multilineAnnotationClassesWithCount.findIndex(
        annotationClass => annotationClass.annotationClass.id === start.class.id
      )
    : 0;

  const startIndexCategory = hasStart
    ? categories.findIndex(category => category === start.category)
    : 0;

  const result = multilineAnnotationClassesWithCount
    .slice(startIndexClass)
    .flatMap(multilineAnnotationClass =>
      categories
        .slice(
          multilineAnnotationClass === multilineAnnotationClassesWithCount[startIndexClass]
            ? startIndexCategory
            : 0
        )
        .map(multilineCategory => ({
          class: multilineAnnotationClass.annotationClass,
          category: multilineCategory
        }))
    )
    .find(
      cell =>
        !(exclude && exclude.class === cell.class && exclude.category === cell.category) &&
        !multilineAnnotations.some(
          annotation =>
            annotation.annotationClassId === cell.class.id &&
            annotation.multilineCategory === cell.category
        )
    );

  return result || null;
}

const ORPHAN = "Orphan";

export function isPointInPolygon(point: LatLngLiteral, polygon: Polygon): boolean {
  const coords = polygon.coordinates[0];
  if (!coords || coords.length === 0) return false;
  return coords.reduce<boolean>((inside, coordI, i) => {
    const coordJ = coords[i === 0 ? coords.length - 1 : i - 1];
    if (!coordI || !coordJ) return inside;
    const xi = coordI[0],
      yi = coordI[1];
    const xj = coordJ[0],
      yj = coordJ[1];
    const intersect =
      yi > point.lng !== yj > point.lng &&
      point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi;
    return intersect ? !inside : inside;
  }, false);
}

function getBoundingBox(
  polygon: Polygon
): { minLat: number; maxLat: number; minLng: number; maxLng: number } | null {
  const coords = polygon.coordinates[0];
  if (!coords || coords.length === 0) return null;
  return coords.reduce(
    (bbox, coord) => {
      if (!coord) return bbox;
      return {
        minLat: Math.min(bbox.minLat, coord[0]),
        maxLat: Math.max(bbox.maxLat, coord[0]),
        minLng: Math.min(bbox.minLng, coord[1]),
        maxLng: Math.max(bbox.maxLng, coord[1])
      };
    },
    { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity }
  );
}

function isPointInBoundingBox(
  point: LatLngLiteral,
  bbox: { minLat: number; maxLat: number; minLng: number; maxLng: number }
): boolean {
  return (
    point.lat >= bbox.minLat &&
    point.lat <= bbox.maxLat &&
    point.lng >= bbox.minLng &&
    point.lng <= bbox.maxLng
  );
}

function getOrphanPoints(
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const myPoints = annotations.filter(
    a => a.annotationType === "POINT" && (a as PointAnnotation).parent === null
  );
  const myPointsNoClass = annotations.filter(
    a =>
      a.annotationType === "POINT" &&
      (a as PointAnnotation).parent === null &&
      !(a as PointAnnotation).annotationClassId
  );

  // Separate manual and AI points
  const manualPoints = myPoints.filter(p => !p.isAi);
  const aiPoints = myPoints.filter(p => p.isAi);
  const manualPointsNoClass = myPointsNoClass.filter(p => !p.isAi);
  const aiPointsNoClass = myPointsNoClass.filter(p => p.isAi);

  const manualOpts = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => (p as PointAnnotation).annotationClassId == cls.id);
      return {
        id: cls.id || "no-id",
        name: cls.name,
        color: cls.color,
        count: pts.length,
        pointIds: pts.map(a => a.id)
      };
    })
    .filter(panno => panno.count > 0);

  const aiOpts = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => (p as PointAnnotation).annotationClassId == cls.id);
      return {
        id: (cls.id || "no-id") + "-ai",
        name: cls.name + "-AI",
        color: cls.color,
        count: pts.length,
        pointIds: pts.map(a => a.id)
      };
    })
    .filter(panno => panno.count > 0);

  const defaultPoints = [
    ...(manualPointsNoClass.length > 0
      ? [
          {
            id: "no-p-cl-id",
            name: "Default",
            color: "#00FF00",
            count: manualPointsNoClass.length,
            pointIds: manualPointsNoClass.map(a => a.id)
          }
        ]
      : []),
    ...(aiPointsNoClass.length > 0
      ? [
          {
            id: "no-p-cl-id-ai",
            name: "Default-AI",
            color: "#00FF00",
            count: aiPointsNoClass.length,
            pointIds: aiPointsNoClass.map(a => a.id)
          }
        ]
      : [])
  ];

  return [...defaultPoints, ...manualOpts, ...aiOpts];
}

function mapStateToUnifiedFragsInternal(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  fhcls: readonly AnnotationClassForm<"FREEHAND">[]
): ReadonlyArray<Fragment> {
  const manualAnnotations = annotations.filter(a => !a.isAi);
  const aiAnnotations = annotations.filter(a => a.isAi);

  const manualFragments = mapStateToFragsOriginal(manualAnnotations, fcls, pcls, annotations);
  const aiFragments = mapStateToFragsOriginal(aiAnnotations, fcls, pcls, annotations).map(
    fragment => ({
      ...fragment,
      name: fragment.name === "HPF + Points" ? "HPF + Points" : fragment.name + "-AI",
      isAI: true,
      hpfs:
        fragment.hpfs?.map(hpf => ({
          ...hpf,
          isAI: true,
          name: hpf.name === ORPHAN ? ORPHAN : hpf.name
        })) || []
    })
  );

  const manualFreehandFragments = buildFreehandFragments(
    manualAnnotations,
    fhcls,
    pcls,
    annotations
  );
  const aiFreehandFragments = buildFreehandFragments(aiAnnotations, fhcls, pcls, annotations).map(
    fragment => ({
      ...fragment,
      name: fragment.name === "Freehand + Points" ? "Freehand + Points" : fragment.name + "-AI",
      isAI: true,
      hpfs:
        fragment.hpfs?.map(fh => ({
          ...fh,
          isAI: true
        })) || []
    })
  );

  // Merge "HPF + Points" fragments
  const otherManual = manualFragments.find(f => f.name === "HPF + Points");
  const otherAI = aiFragments.find(f => f.name === "HPF + Points");

  const mergedHpfFragments = (() => {
    if (otherManual && otherAI) {
      const manualOrphan = otherManual.hpfs?.find(h => h.name === ORPHAN);
      const aiOrphan = otherAI.hpfs?.find(h => h.name === ORPHAN);
      const manualNonOrphan = otherManual.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const aiNonOrphan = otherAI.hpfs?.filter(h => h.name !== ORPHAN) || [];

      const mergedOrphan =
        manualOrphan && aiOrphan
          ? [
              {
                ...manualOrphan,
                points: [...(manualOrphan.points || []), ...(aiOrphan.points || [])],
                count: (manualOrphan.count || 0) + (aiOrphan.count || 0)
              }
            ]
          : manualOrphan
          ? [manualOrphan]
          : aiOrphan
          ? [aiOrphan]
          : [];

      const mergedHpfs = [...mergedOrphan, ...manualNonOrphan, ...aiNonOrphan];

      return [
        {
          ...otherManual,
          hpfs: mergedHpfs
        }
      ];
    } else if (otherManual) {
      // Ensure Orphan is first even when only manual exists
      const orphan = otherManual.hpfs?.find(h => h.name === ORPHAN);
      const nonOrphan = otherManual.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const sortedHpfs = orphan ? [orphan, ...nonOrphan] : nonOrphan;

      return [
        {
          ...otherManual,
          hpfs: sortedHpfs
        }
      ];
    } else if (otherAI) {
      // Ensure Orphan is first even when only AI exists
      const orphan = otherAI.hpfs?.find(h => h.name === ORPHAN);
      const nonOrphan = otherAI.hpfs?.filter(h => h.name !== ORPHAN) || [];
      const sortedHpfs = orphan ? [orphan, ...nonOrphan] : nonOrphan;

      return [
        {
          ...otherAI,
          hpfs: sortedHpfs
        }
      ];
    }
    return [];
  })();

  // Merge "Freehand + Points" fragments
  const freehandManual = manualFreehandFragments.find(f => f.name === "Freehand + Points");
  const freehandAI = aiFreehandFragments.find(f => f.name === "Freehand + Points");

  const mergedFreehandFragments = (() => {
    if (freehandManual && freehandAI) {
      return [
        {
          ...freehandManual,
          hpfs: [...(freehandManual.hpfs || []), ...(freehandAI.hpfs || [])]
        }
      ];
    } else if (freehandManual) {
      return [freehandManual];
    } else if (freehandAI) {
      return [freehandAI];
    }
    return [];
  })();

  return [
    ...mergedHpfFragments,
    ...mergedFreehandFragments,
    ...manualFragments.filter(f => f.name !== "HPF + Points"),
    ...aiFragments.filter(f => f.name !== "HPF + Points"),
    ...manualFreehandFragments.filter(f => f.name !== "Freehand + Points"),
    ...aiFreehandFragments.filter(f => f.name !== "Freehand + Points")
  ];
}

function mapStateToFragsOriginal(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  allAnnotationsForPoints: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  const orphanPoints = getOrphanPoints(pcls, annotations);

  // Create fragments from HPF classes
  const classifiedFragments = fcls
    .map(fc => {
      const hpfs = annotations
        .filter(a => a.annotationType === "HPF" && (a as HPFAnnotation).annotationClassId === fc.id)
        .map(hpf => {
          const hpfAnnotation = hpf as HPFAnnotation;
          const pts = getPointsForHpf(hpfAnnotation, pcls, allAnnotationsForPoints);
          return {
            id: hpf.id || "no-id",
            name: "",
            count: pts.reduce((s, c) => s + c.count, 0),
            color: hpfAnnotation.color || fc.color,
            isOpen: false,
            isAI: false,
            points: pts
          };
        });
      return {
        id: fc.id,
        name: fc.name,
        count: hpfs.reduce((s, c) => s + c.count, 0),
        color: fc.color,
        isOpen: true,
        isAI: false,
        hpfs
      };
    })
    .filter(f => f.hpfs.length > 0);

  const defaultHpfs = annotations
    .filter(a => a.annotationType === "HPF" && !(a as HPFAnnotation).annotationClassId)
    .map(hpf => {
      const hpfAnnotation = hpf as HPFAnnotation;
      const pts = getPointsForHpf(hpfAnnotation, pcls, allAnnotationsForPoints);
      return {
        id: hpf.id || "no-id",
        name: "",
        count: pts.reduce((s, c) => s + c.count, 0),
        color: hpfAnnotation.color || "#00FF00",
        isOpen: false,
        isAI: false,
        points: pts
      };
    });

  // Build "Other" fragment HPFs
  const orphanHpf = orphanPoints.length > 0 ? [createOrphanHpf(orphanPoints)] : [];
  const otherHpfs = [...orphanHpf, ...defaultHpfs];

  // Return fragments with "Other" if needed
  if (otherHpfs.length === 0) {
    return classifiedFragments;
  }

  return [createOtherFragment(otherHpfs), ...classifiedFragments];
}

function getPointsForHpf(
  hpf: HPFAnnotation,
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const myPoints = annotations.filter(
    a => a.annotationType === "POINT" && (a as PointAnnotation).parent === hpf.id
  );

  const manualPoints = myPoints.filter(p => !p.isAi);
  const aiPoints = myPoints.filter(p => p.isAi);

  const classifiedManualPoints = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => (p as PointAnnotation).annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name,
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const classifiedAiPoints = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => (p as PointAnnotation).annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name + "-AI",
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const defaultManualPoints = manualPoints.filter(p => !(p as PointAnnotation).annotationClassId);
  const defaultManualPointAnno =
    defaultManualPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default",
            color: "#FFFF00",
            count: defaultManualPoints.length,
            pointIds: defaultManualPoints.map(a => a.id)
          }
        ]
      : [];

  const defaultAiPoints = aiPoints.filter(p => !(p as PointAnnotation).annotationClassId);
  const defaultAiPointAnno =
    defaultAiPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default-AI",
            color: "#FFFF00",
            count: defaultAiPoints.length,
            pointIds: defaultAiPoints.map(a => a.id)
          }
        ]
      : [];

  return [
    ...defaultManualPointAnno,
    ...classifiedManualPoints,
    ...defaultAiPointAnno,
    ...classifiedAiPoints
  ];
}

function createOrphanHpf(pts: ReadonlyArray<PointAnno>): HpfAnno {
  return {
    id: "no-id2",
    name: ORPHAN,
    count: pts.reduce((s, c) => s + c.count, 0),
    color: "black",
    isOpen: false,
    isAI: false,
    points: pts
  };
}

function createOtherFragment(hpfs: ReadonlyArray<HpfAnno>): Fragment {
  return {
    id: "no-id",
    name: "HPF + Points",
    count: 0,
    color: "black",
    isOpen: true,
    isAI: false,
    hpfs
  };
}

function buildFreehandFragments(
  annotations: ReadonlyArray<Annotation>,
  fhcls: readonly AnnotationClassForm<"FREEHAND">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  allAnnotations: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  const freehandAnnotations = annotations.filter(
    a => a.annotationType === "FREEHAND"
  ) as ReadonlyArray<FreehandAnnotation>;

  if (freehandAnnotations.length === 0) return [];

  const classifiedFragments = fhcls
    .map(fc => {
      const freehands = freehandAnnotations
        .filter(fh => fh.annotationClassId === fc.id)
        .map(fh => {
          const pts = getPointsForFreehand(fh, pcls, allAnnotations);
          return {
            id: fh.id || "no-id",
            name: "",
            count: pts.reduce((s, c) => s + c.count, 0),
            color: fh.color || fc.color,
            isOpen: false,
            isAI: false,
            points: pts
          };
        });
      return {
        id: fc.id,
        name: fc.name,
        count: freehands.reduce((s, c) => s + c.count, 0),
        color: fc.color,
        isOpen: true,
        isAI: false,
        hpfs: freehands
      };
    })
    .filter(f => f.hpfs.length > 0);

  const defaultFreehands = freehandAnnotations
    .filter(fh => !fh.annotationClassId)
    .map(fh => {
      const pts = getPointsForFreehand(fh, pcls, allAnnotations);
      return {
        id: fh.id || "no-id",
        name: "",
        count: pts.reduce((s, c) => s + c.count, 0),
        color: fh.color || "#FFFF00",
        isOpen: false,
        isAI: false,
        points: pts
      };
    });

  if (defaultFreehands.length === 0 && classifiedFragments.length === 0) {
    return [];
  }

  const otherFreehands =
    defaultFreehands.length > 0
      ? [
          {
            id: "no-id",
            name: "Freehand + Points",
            count: 0,
            color: "black",
            isOpen: true,
            isAI: false,
            hpfs: defaultFreehands
          }
        ]
      : [];

  return [...otherFreehands, ...classifiedFragments];
}

// Whether a point annotation lies inside a freehand region (bounding-box pre-check, then
// point-in-polygon). Shared by the annotation tree and the IEL ratio so both agree on membership.
function isPointInFreehand(point: PointAnnotation, freehand: FreehandAnnotation): boolean {
  const bbox = getBoundingBox(freehand.geometry);
  const coords = point.geometry.coordinates;
  const latLng = { lat: coords[0], lng: coords[1] };
  return (
    bbox !== null &&
    isPointInBoundingBox(latLng, bbox) &&
    isPointInPolygon(latLng, freehand.geometry)
  );
}

function getPointsForFreehand(
  freehand: FreehandAnnotation,
  pcls: readonly AnnotationClassForm<"POINT">[],
  annotations: ReadonlyArray<Annotation>
): ReadonlyArray<PointAnno> {
  const allPoints = annotations.filter(
    a => a.annotationType === "POINT"
  ) as ReadonlyArray<PointAnnotation>;

  const pointsInside = allPoints.filter(p => isPointInFreehand(p, freehand));

  const manualPoints = pointsInside.filter(p => !p.isAi);
  const aiPoints = pointsInside.filter(p => p.isAi);

  const classifiedManualPoints = pcls
    .map(cls => {
      const pts = manualPoints.filter(p => p.annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name,
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const classifiedAiPoints = pcls
    .map(cls => {
      const pts = aiPoints.filter(p => p.annotationClassId === cls.id);
      return pts.length > 0
        ? {
            id: cls.id || "no-id",
            name: cls.name + "-AI",
            color: cls.color,
            count: pts.length,
            pointIds: pts.map(a => a.id)
          }
        : null;
    })
    .filter((panno): panno is PointAnno => panno !== null);

  const defaultManualPoints = manualPoints.filter(p => !p.annotationClassId);
  const defaultManualPointAnno =
    defaultManualPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default",
            color: "#FFFF00",
            count: defaultManualPoints.length,
            pointIds: defaultManualPoints.map(a => a.id)
          }
        ]
      : [];

  const defaultAiPoints = aiPoints.filter(p => !p.annotationClassId);
  const defaultAiPointAnno =
    defaultAiPoints.length > 0
      ? [
          {
            id: "no-id",
            name: "Default-AI",
            color: "#FFFF00",
            count: defaultAiPoints.length,
            pointIds: defaultAiPoints.map(a => a.id)
          }
        ]
      : [];

  return [
    ...defaultManualPointAnno,
    ...classifiedManualPoints,
    ...defaultAiPointAnno,
    ...classifiedAiPoints
  ];
}

export function mapStateToFrags(
  annotations: ReadonlyArray<Annotation>,
  fcls: readonly AnnotationClassForm<"HPF">[],
  pcls: readonly AnnotationClassForm<"POINT">[],
  _allAnnotationsForPoints?: ReadonlyArray<Annotation>
): ReadonlyArray<Fragment> {
  return mapStateToFragsOriginal(annotations, fcls, pcls, annotations);
}

const NO_FRAGMENTS: ReadonlyArray<Fragment> = [];

// Building the fragment tree is superlinear in annotation count (points are grouped per HPF, and
// point-in-polygon containment is tested per freehand), so it must not run on every dispatched
// action.  Memoizing on the image resource means it recomputes only when the annotation set
// actually changes. That is safe because Immer gives the resource a new reference for any write to
// it (imageFetch/refreshImage replace it; toggleAcceptAiAnnotations assigns a field in place), while
// the highlight/hover reducers only read it. The guarantee rests on Immer, not on writes being
// whole-object replacements, so a mutation made outside Immer would not invalidate this.
const unifiedFragsSelector = createSelector(
  [(state: RootState) => state.caseImageViewer.imageWithAnnotations],
  (imageWithAnnotations): ReadonlyArray<Fragment> => {
    if (!imageWithAnnotations || !("resource" in imageWithAnnotations)) {
      return NO_FRAGMENTS;
    }

    const imageResource = imageWithAnnotations.resource;
    const { annotations } = imageResource;

    // Derive annotation classes from the image response itself, which always contains the
    // annotation classes for the study that the image belongs to.  This avoids a dependency on
    // studyConfiguration state (populated via a separate studyFetchRequest call) which may not
    // yet be loaded — or may never be populated for non-admin users whose study response does not
    // include the full admin view.
    const toClassForm = <T extends AnnotationClassType>(
      ac: AnnotationClassWithCount<T>
    ): AnnotationClassForm<T> => ({
      ...ac.annotationClass,
      count: ac.count,
      deleted: false
    });

    const fragClasses = imageResource.hpfAnnotationClassesWithCount.map(toClassForm);
    const pointClasses = imageResource.pointAnnotationClassesWithCount.map(toClassForm);
    const freehandClasses = imageResource.freehandAnnotationClassesWithCount.map(toClassForm);

    return mapStateToUnifiedFragsInternal(annotations, fragClasses, pointClasses, freehandClasses);
  }
);

export function mapStateToUnifiedFrags(state: RootState): ReadonlyArray<Fragment> {
  return unifiedFragsSelector(state);
}

export function mapStateToFragments(state: RootState): ReadonlyArray<Fragment> {
  return mapStateToUnifiedFrags(state);
}

export function convertCoordinatesToLatLngLiteral(coordinates: number[]): LatLngLiteral {
  return {
    lat: coordinates[0] || 0,
    lng: coordinates[1] || 0
  };
}

export interface PointClassIds {
  readonly hpfId: string;
  readonly pointClassId: string;
  readonly hpfClassId: string | undefined;
}

/**
 * Reverse index from annotation id back to the fragment tree.
 *
 * Resolving these by scanning the fragment tree once per annotation is O(annotations x fragments x
 * hpfs x points), which was tolerable while the annotation controls were built once per image but
 * is not now that the mounted set is rebuilt whenever the viewport settles.
 */
export interface FragmentLookup {
  // Point annotation id -> the hpf and classes it belongs to.
  readonly pointClasses: ReadonlyMap<string, PointClassIds>;
  // HPF annotation id -> the id of the fragment containing it.
  readonly hpfClassIds: ReadonlyMap<string, string | undefined>;
}

export function buildFragmentLookup(fragments: readonly Fragment[]): FragmentLookup {
  const hpfEntries = fragments.flatMap(fragment =>
    (fragment.hpfs || []).map(hpf => [hpf.id, fragment.id] as const)
  );

  const pointEntries = fragments.flatMap(fragment =>
    (fragment.hpfs || []).flatMap(hpf =>
      (hpf.points || []).flatMap(pointClass =>
        pointClass.pointIds.map(
          pointId =>
            [
              pointId,
              { hpfId: hpf.id, pointClassId: pointClass.id, hpfClassId: fragment.id }
            ] as const
        )
      )
    )
  );

  // The scans this replaces used `find`, which stops at the first match; `new Map` keeps the last
  // entry for a duplicate key, so reverse to preserve first-wins for ids appearing more than once.
  return {
    pointClasses: new Map(pointEntries.slice().reverse()),
    hpfClassIds: new Map(hpfEntries.slice().reverse())
  };
}

/* Viewport culling ------------------------------------------------------------------------------
 *
 * The image viewer mounts one Leaflet layer per annotation, so the cost of a pan or zoom scales
 * with the total annotation count unless off-screen annotations are left unmounted. These helpers
 * precompute an axis-aligned bounding box per annotation once per annotation set so the per-frame
 * work is a cheap rectangle overlap test. Depends on `cachedMap` (below): before that existed,
 * changing the mounted set on every viewport settle meant rebuilding every visible annotation's
 * element from scratch, which measured far slower than not culling at all.
 *
 * NOTE: annotation geometry coordinates are `[lat, lng]` pairs (see
 * `convertCoordinatesToLatLngLiteral`) -- the opposite ordering to the image extent polygon
 * consumed by `toLatLngBounds`.
 */

export interface AnnotationBoundingBox {
  readonly minLat: number;
  readonly maxLat: number;
  readonly minLng: number;
  readonly maxLng: number;
}

export interface IndexedAnnotation {
  readonly annotation: Annotation;
  // Null when the annotation's geometry carries no usable coordinates; such annotations are never
  // culled since there is nothing to test them against.
  readonly boundingBox: AnnotationBoundingBox | null;
}

// Leaflet circle and ellipse radii are in metres. Converting them to degrees only has to be good
// enough to answer "is this near the viewport"; the padding applied to the viewport bounds absorbs
// the error, so the usual spherical approximation is used instead of a projection round-trip.
const METERS_PER_DEGREE_LATITUDE = 111320;

function metersToDegreesLatitude(meters: number): number {
  return meters / METERS_PER_DEGREE_LATITUDE;
}

function metersToDegreesLongitude(meters: number, latitude: number): number {
  const scale = Math.cos((latitude * Math.PI) / 180);
  return Math.abs(scale) < 1e-9 ? 180 : meters / (METERS_PER_DEGREE_LATITUDE * scale);
}

function boundingBoxOfCoordinates(
  coordinates: ReadonlyArray<readonly [number, number]>
): AnnotationBoundingBox | null {
  return coordinates.reduce<AnnotationBoundingBox | null>(
    (bbox, [lat, lng]) =>
      bbox === null
        ? { minLat: lat, maxLat: lat, minLng: lng, maxLng: lng }
        : {
            minLat: Math.min(bbox.minLat, lat),
            maxLat: Math.max(bbox.maxLat, lat),
            minLng: Math.min(bbox.minLng, lng),
            maxLng: Math.max(bbox.maxLng, lng)
          },
    null
  );
}

function boundingBoxOfCircle(
  center: readonly [number, number],
  radiusInMeters: number
): AnnotationBoundingBox {
  const [lat, lng] = center;
  const latDelta = metersToDegreesLatitude(radiusInMeters);
  const lngDelta = metersToDegreesLongitude(radiusInMeters, lat);
  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLng: lng - lngDelta,
    maxLng: lng + lngDelta
  };
}

/**
 * Bounding box of a single annotation.
 *
 * `pointRadiusInMeters` is the on-map radius drawn for point and text annotations, whose geometry
 * is a bare coordinate; passing it keeps a point that is just off the edge of the viewport from
 * being culled while part of its marker would still be visible.
 *
 * NOTE: the shape checks below mirror the ones `loadAnnotationControls` (ImageMap.tsx) uses to pick
 * a renderer for each annotation -- keep the two in step.
 */
export function annotationBoundingBox(
  annotation: Annotation,
  pointRadiusInMeters: number
): AnnotationBoundingBox | null {
  return "radius" in annotation
    ? boundingBoxOfCircle(annotation.geometry.coordinates, annotation.radius)
    : "tilt" in annotation
    ? boundingBoxOfCircle(
        annotation.geometry.coordinates,
        Math.max(annotation.radiusX, annotation.radiusY)
      )
    : annotation.geometry.type === "Polygon"
    ? boundingBoxOfCoordinates(annotation.geometry.coordinates.flat())
    : annotation.geometry.type === "LineString"
    ? boundingBoxOfCoordinates(annotation.geometry.coordinates)
    : boundingBoxOfCircle(annotation.geometry.coordinates, pointRadiusInMeters);
}

export function indexAnnotationsByBoundingBox(
  annotations: ReadonlyArray<Annotation>,
  pointRadiusInMeters: number
): ReadonlyArray<IndexedAnnotation> {
  return annotations.map(annotation => ({
    annotation: annotation,
    boundingBox: annotationBoundingBox(annotation, pointRadiusInMeters)
  }));
}

export function boundingBoxIntersects(
  boundingBox: AnnotationBoundingBox,
  bounds: LatLngBounds
): boolean {
  return (
    boundingBox.maxLat >= bounds.getSouth() &&
    boundingBox.minLat <= bounds.getNorth() &&
    boundingBox.maxLng >= bounds.getWest() &&
    boundingBox.minLng <= bounds.getEast()
  );
}

/**
 * The annotations that need to be mounted for the given (already padded) viewport bounds.
 *
 * `alwaysVisibleIds` escapes the cull for annotations that must stay mounted regardless of where
 * the viewport is -- currently the ones highlighted from the sidebar. A null `bounds` means the map
 * has not reported its viewport yet, in which case nothing is culled.
 */
export function annotationsInBounds(
  indexedAnnotations: ReadonlyArray<IndexedAnnotation>,
  bounds: LatLngBounds | null,
  alwaysVisibleIds: ReadonlySet<UUID>
): ReadonlyArray<Annotation> {
  return bounds === null
    ? indexedAnnotations.map(indexed => indexed.annotation)
    : indexedAnnotations
        .filter(
          indexed =>
            indexed.boundingBox === null ||
            alwaysVisibleIds.has(indexed.annotation.id) ||
            boundingBoxIntersects(indexed.boundingBox, bounds)
        )
        .map(indexed => indexed.annotation);
}

interface CacheEntry<V> {
  readonly deps: ReadonlyArray<unknown>;
  readonly value: V;
}

export type ElementCache<V> = ReadonlyMap<string, CacheEntry<V>>;

function sameDeps(a: ReadonlyArray<unknown>, b: ReadonlyArray<unknown>): boolean {
  return a.length === b.length && a.every((dep, index) => Object.is(dep, b[index]));
}

/**
 * Renders each item, reusing the previous call's rendered value for any item whose id and dep
 * array are unchanged from last time. `loadAnnotationControls` (ImageMap.tsx) otherwise builds a
 * brand-new element for every annotation on every call, so React can never bail out of
 * reconciling an annotation's subtree even when nothing about it changed -- at a few thousand
 * annotations this is a large share of a re-render's cost. `idOf`/`depsOf` must cover every value
 * `render` reads; anything omitted from `deps` will read stale on a cache hit.
 */
export function cachedMap<T, V>(
  cache: ElementCache<V>,
  items: ReadonlyArray<T>,
  idOf: (item: T) => string,
  depsOf: (item: T) => ReadonlyArray<unknown>,
  render: (item: T) => V
): { readonly values: ReadonlyArray<V>; readonly cache: ElementCache<V> } {
  const entries: ReadonlyArray<readonly [string, CacheEntry<V>]> = items.map(item => {
    const id = idOf(item);
    const deps = depsOf(item);
    const previous = cache.get(id);
    const value = previous && sameDeps(previous.deps, deps) ? previous.value : render(item);
    return [id, { deps, value }] as const;
  });

  return {
    values: entries.map(([, entry]) => entry.value),
    cache: new Map(entries)
  };
}

/* Highlight weight ------------------------------------------------------------------------------
 *
 * Point and HPF annotations are drawn heavier while their sidebar row is hovered. That used to be
 * done by copying every annotation in the store with a new `weight` on each hover; it is derived
 * here instead, from the set of highlighted ids, so a hover only changes the annotations involved.
 */

export const DEFAULT_ANNOTATION_WEIGHT = 5;
export const HIGHLIGHTED_ANNOTATION_WEIGHT = 10;

export function annotationWeight(
  highlightedIds: ReadonlySet<string>,
  annotationId: string
): number {
  return highlightedIds.has(annotationId)
    ? HIGHLIGHTED_ANNOTATION_WEIGHT
    : DEFAULT_ANNOTATION_WEIGHT;
}

// LUC-305: natural, case-insensitive string comparison. Digit runs compare by numeric value
// ("Distal 2" before "Distal 10"), everything else by lower-cased code unit. Mirrors the backend
// ImageOrdering.compareNatural exactly (NOT localeCompare, whose punctuation rules differ), so the
// case viewer and the API order images identically.
const NATURAL_CHUNK = /[0-9]+|[^0-9]+/g;
const compareCodeUnits = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const isDigitRun = (chunk: string): boolean => /^[0-9]/.test(chunk);
const compareNaturalChunks = (x: string, y: string): number => {
  if (isDigitRun(x) && isDigitRun(y)) {
    // By value without overflow: drop leading zeros, then the longer run is larger.
    const xn = x.replace(/^0+/, "");
    const yn = y.replace(/^0+/, "");
    return xn.length !== yn.length ? xn.length - yn.length : compareCodeUnits(xn, yn);
  }
  return compareCodeUnits(x, y);
};
export const compareNatural = (a: string, b: string): number => {
  const xs = a.toLowerCase().match(NATURAL_CHUNK) ?? [];
  const ys = b.toLowerCase().match(NATURAL_CHUNK) ?? [];
  const firstDifference = xs
    .slice(0, Math.min(xs.length, ys.length))
    .map((x, idx) => compareNaturalChunks(x, ys[idx] ?? ""))
    .find(result => result !== 0);
  return firstDifference ?? xs.length - ys.length;
};

// LUC-305: the default order of a case's images, mirroring the backend ImageOrdering: sort order
// (nulls last; only set by a manual reorder), then anatomical segment (natural, case-insensitive;
// missing/blank last), then name (natural; orders the members of an H&E biopsy group), then id.
export const compareImages = (a: Image, b: Image): number => {
  const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }
  const aSegment = a.biopsyLocation?.trim() ?? "";
  const bSegment = b.biopsyLocation?.trim() ?? "";
  const bySegment =
    aSegment !== "" && bSegment !== ""
      ? compareNatural(aSegment, bSegment)
      : aSegment !== ""
      ? -1
      : bSegment !== ""
      ? 1
      : 0;
  if (bySegment !== 0) {
    return bySegment;
  }
  const byName = compareNatural(a.name, b.name);
  return byName !== 0 ? byName : compareCodeUnits(a.id, b.id);
};

// LUC-305: move one reorder unit (a single image, or a whole biopsy group) one place up (-1) or
// down (1), then renumber EVERY image 1..n in the new order, so a single click fixes the manual
// position of all images. A group's images stay contiguous and keep their order; the backend orders
// biopsies by their first image, so this matches what it returns. Null when there is no neighbour.
export const moveOrderUnit = (
  units: ReadonlyArray<ReadonlyArray<HasId>>,
  unitIndex: number,
  direction: 1 | -1
): ReadonlyArray<{ readonly imageId: UUID; readonly sortOrder: number }> | null => {
  const target = unitIndex + direction;
  const current = units[unitIndex];
  const neighbour = units[target];
  return current && neighbour
    ? units
        .map((unit, k) => (k === unitIndex ? neighbour : k === target ? current : unit))
        .flatMap(unit => unit)
        .map((image, idx) => ({ imageId: image.id, sortOrder: idx + 1 }))
    : null;
};
